import {
  err,
  extractWatchPageFacts,
  ok,
  parseWatchPageDetails,
  resolveInstanceUrl,
  withQuery,
  type AbortLike,
  type Result,
  type VideoDetails,
} from '@visious/core';
import type { Platform } from '../platform/instance';
import { readJsonBody } from './json';
import type { ApiProblem } from './problem';

/**
 * `GET /watch?v={id}` — the instance's own page, read for the record `/api/v1/videos/{id}` did not
 * give (docs/api/video.md §2: some videos answer that endpoint `200` with zero bytes while the
 * page still plays, through the companion). Same module shape as `api/videos.ts`: the URL through
 * core (R2), the request through the transport, and the parse split on the R4 boundary — core
 * hands the two embedded JSON blocks back as text and this is where `JSON.parse` runs.
 */
export async function fetchWatchPageDetails(
  platform: Platform,
  videoId: string,
  // `AbortLike`, not `AbortSignal`: screen models call this and must stay DOM-free (R13).
  signal?: AbortLike,
): Promise<Result<VideoDetails, ApiProblem>> {
  const path = '/watch';
  const base = resolveInstanceUrl(platform.config, path);
  if (base === null) return err({ kind: 'bad-url', path });
  const url = withQuery(base, { v: videoId });
  if (url === null) return err({ kind: 'bad-url', path });

  const response = await platform.transport.send({ method: 'GET', url, headers: {}, signal });
  if (!response.ok) return err({ kind: 'transport', error: response.error });
  if (response.value.body.trim() === '') return err({ kind: 'empty-response' });

  const facts = extractWatchPageFacts(response.value.body);
  if (!facts.ok) return err({ kind: 'parse', error: facts.error });

  const parsed = parseWatchPageDetails(
    {
      videoId,
      facts: facts.value,
      playerData: embeddedJson(facts.value.playerDataJson),
      videoData: embeddedJson(facts.value.videoDataJson),
    },
    platform.parseContext,
  );
  if (!parsed.ok) return err({ kind: 'parse', error: parsed.error });

  return ok(parsed.value);
}

/**
 * One embedded block, or `null` for one that is absent or unreadable — per block rather than
 * failing the page, since `parseWatchPageDetails` can assemble from either alone and refuses only
 * when both are gone.
 */
function embeddedJson(text: string | null): unknown {
  if (text === null) return null;
  const read = readJsonBody(text);
  return read.ok ? read.value : null;
}
